import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { SAUDI_CITIES } from "@/data/saudi_locations";

const DEFAULT_LOCATION = {
  city_id: "bahrah",
  city_ar: "بحرة",
  city_en: "Bahrah",
  district_ar: "حي المروج",
  district_en: "Al Muruj",
  lat: 21.4038,
  lng: 39.4494,
  street: "",
};

const LocationContext = createContext(null);

// GPS: detect nearest city by lat/lng
export const detectCityFromCoords = (lat, lng) => {
  let nearest = SAUDI_CITIES[0];
  let minDist = Infinity;
  for (const c of SAUDI_CITIES) {
    const d = Math.hypot(c.lat - lat, c.lng - lng);
    if (d < minDist) { minDist = d; nearest = c; }
  }
  return nearest;
};

export function LocationProvider({ children }) {
  const [location, setLocationState] = useState(() => {
    try {
      const saved = localStorage.getItem("jh_location");
      return saved ? JSON.parse(saved) : DEFAULT_LOCATION;
    } catch { return DEFAULT_LOCATION; }
  });
  // City the device is physically in (from GPS). Users may only order within it.
  const [gpsCity, setGpsCity] = useState(null);
  const [gpsStatus, setGpsStatus] = useState("idle"); // idle | locating | ok | denied | unavailable

  useEffect(() => {
    localStorage.setItem("jh_location", JSON.stringify(location));
  }, [location]);

  const setLocation = (loc) => setLocationState((prev) => ({ ...prev, ...loc }));

  // Resolve device GPS, lock the city, and snap the saved location into that city if it isn't already.
  const locateGps = useCallback(() => new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      setGpsStatus("unavailable");
      reject(new Error("unsupported"));
      return;
    }
    setGpsStatus("locating");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const ll = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        const city = detectCityFromCoords(ll.lat, ll.lng);
        setGpsCity(city);
        setGpsStatus("ok");
        setLocationState((prev) => prev.city_id === city.id ? prev : {
          city_id: city.id,
          city_ar: city.name_ar,
          city_en: city.name_en,
          district_ar: city.districts[0].ar,
          district_en: city.districts[0].en,
          lat: ll.lat,
          lng: ll.lng,
          street: "",
        });
        resolve(ll);
      },
      (err) => {
        setGpsStatus(err.code === 1 ? "denied" : "unavailable");
        reject(err);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  }), []);

  useEffect(() => { locateGps().catch(() => {}); }, [locateGps]);

  return (
    <LocationContext.Provider value={{ location, setLocation, detectCityFromCoords, gpsCity, gpsStatus, locateGps }}>
      {children}
    </LocationContext.Provider>
  );
}

export const useLocation = () => {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error("useLocation must be inside LocationProvider");
  return ctx;
};
